import { translateLabel } from "@/i18n/messages";
import React, { useEffect, useMemo, useState } from "react";
import { CaretDownIcon, CaretRightIcon } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import { TrailRow } from "@/components/TrailRow";

const DESKTOP_PREVIEW_LIMIT = 3;
const MOBILE_PREVIEW_LIMIT = 2;

export function TrailCatalog({
  groups,
  activeGroupFilter = "all",
  selectedTrailId,
  hoveredTrailId,
  onHover,
  onSelect,
  registerRow,
  compact = false,
}) {
  const [expandedGroups, setExpandedGroups] = useState({});

  const selectedGroupId = useMemo(
    () => groups.find((group) => group.trails.some((trail) => trail.id === selectedTrailId))?.id ?? null,
    [groups, selectedTrailId],
  );

  useEffect(() => {
    if (!selectedGroupId || !selectedTrailId) return;
    setExpandedGroups((current) => ({ ...current, [selectedGroupId]: true }));

    const scrollSelectedIntoView = () => {
      const id = String(selectedTrailId);
      const selector = typeof CSS !== "undefined" && CSS.escape
        ? `[data-trail-id="${CSS.escape(id)}"]`
        : `[data-trail-id="${id.replaceAll('"', '\\"')}"]`;
      const node = document.querySelector(selector);
      node?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
    };

    const frame = window.requestAnimationFrame(() => window.requestAnimationFrame(scrollSelectedIntoView));
    return () => window.cancelAnimationFrame(frame);
  }, [selectedGroupId, selectedTrailId]);

  const toggleGroup = (groupId) => {
    setExpandedGroups((current) => ({ ...current, [groupId]: !current[groupId] }));
  };

  if (compact) {
    const trails = groups.flatMap((group) => group.trails);
    return (
      <div className="space-y-2 rounded-2xl border border-white/[0.08] bg-[#0e0e10] p-2 sm:p-3" data-testid="trail-catalog">
        {trails.map((trail, trailIndex) => (
          <motion.div
            key={trail.id}
            ref={(node) => registerRow?.(trail.id, node)}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(trailIndex * 0.025, 0.12) }}
          >
            <TrailRow
              trail={trail}
              selected={selectedTrailId === trail.id}
              hovered={hoveredTrailId === trail.id}
              onHover={onHover}
              onSelect={onSelect}
            />
          </motion.div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="trail-catalog">
      {groups.map((group, groupIndex) => {
        const forceExpanded = activeGroupFilter !== "all";
        const expanded = forceExpanded || expandedGroups[group.id] === true;
        const visibleTrails = expanded ? group.trails : group.trails.slice(0, DESKTOP_PREVIEW_LIMIT);
        const hasDesktopMore = group.trails.length > DESKTOP_PREVIEW_LIMIT;
        const hasMobileMore = group.trails.length > MOBILE_PREVIEW_LIMIT;
        const containsSelection = group.id === selectedGroupId;

        return (
          <motion.section
            layout
            key={group.id}
            data-testid={`trail-catalog-group-${group.id}`}
            className={`overflow-hidden rounded-2xl border bg-[#0e0e10] transition-colors ${
              containsSelection ? "border-[#CCFF00]/30" : "border-white/[0.08]"
            }`}
          >
            <div className="flex items-center justify-between gap-3 border-b border-white/[0.07] px-4 py-3 sm:px-5">
              <div className="min-w-0">
                <div className="flex items-center gap-2.5">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: group.color }} />
                  <h4 className="font-display text-lg font-bold uppercase text-white sm:text-xl">{translateLabel(group.label)}</h4>
                  <span className="text-xs font-semibold text-zinc-500">{group.trails.length} {translateLabel(group.trails.length === 1 ? "trail" : "trails")}</span>
                  {containsSelection && (
                    <span className="hidden rounded-full bg-[#CCFF00]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#CCFF00] sm:inline-flex">
                      {translateLabel("Map selection")}
                    </span>
                  )}
                </div>
                <p className="mt-0.5 hidden truncate text-xs text-zinc-500 sm:block">{translateLabel(group.description)}</p>
              </div>

              {activeGroupFilter === "all" && (
                <>
                  {hasMobileMore && (
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.id)}
                      className="flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-bold text-zinc-300 transition-colors hover:bg-white/[0.06] hover:text-white sm:hidden"
                      aria-expanded={expanded}
                      data-testid={`trail-catalog-toggle-${group.id}`}
                    >
                      {expanded ? translateLabel("Less") : `${translateLabel("All")} ${group.trails.length}`}
                      {expanded ? <CaretDownIcon size={14} /> : <CaretRightIcon size={14} />}
                    </button>
                  )}
                  {hasDesktopMore && (
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.id)}
                      className="hidden shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-bold text-zinc-300 transition-colors hover:bg-white/[0.06] hover:text-white sm:flex"
                      aria-expanded={expanded}
                      data-testid={`trail-catalog-toggle-desktop-${group.id}`}
                    >
                      {expanded ? translateLabel("Show less") : `${translateLabel("See all")} ${group.trails.length}`}
                      {expanded ? <CaretDownIcon size={14} /> : <CaretRightIcon size={14} />}
                    </button>
                  )}
                </>
              )}
            </div>

            <motion.div layout className="space-y-2 p-2 sm:p-3">
              <AnimatePresence initial={false}>
                {visibleTrails.map((trail, trailIndex) => {
                  const hideThirdOnMobile = !expanded && trailIndex >= MOBILE_PREVIEW_LIMIT;
                  return (
                    <motion.div
                      layout
                      key={trail.id}
                      ref={(node) => registerRow?.(trail.id, node)}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.16, delay: Math.min((groupIndex * 2 + trailIndex) * 0.015, 0.12) }}
                      className={hideThirdOnMobile ? "hidden sm:block" : ""}
                    >
                      <TrailRow
                        trail={trail}
                        selected={selectedTrailId === trail.id}
                        hovered={hoveredTrailId === trail.id}
                        onHover={onHover}
                        onSelect={onSelect}
                      />
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          </motion.section>
        );
      })}
    </div>
  );
}
